import "./OrgSwitcher.scss";

import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { API_ROUTES } from "@/config/apiRoutes";
import { getPagePath, PAGES } from "@/config/pages";
import { StyledDropdown } from "@/covaltech-react-ui";
import { isSupportedLanguage } from "@/i18n/routing";
import { useAuthStore } from "@/stores/AuthStore";
import {
  type Organisation,
  useOrganisationStore,
} from "@/stores/OrganisationStore";
import { loggerService, LogTag } from "@/Utils/LoggerService";
import appFetch from "@/Utils/Services/Fetch/appFetch";

interface OrgSwitcherProps {
  mobile?: boolean;
  /** Injected by DesktopNavbar when rendered as `beforeChildren`. */
  open?: boolean;
}

interface ListUserOrganisationsResponse {
  data: Organisation[];
}

const OrgSwitcher = ({ mobile = false, open = true }: OrgSwitcherProps) => {
  const { t } = useTranslation();
  const { lang } = useParams<{ lang: string }>();
  const { user, setUser } = useAuthStore();
  const { organisation, organisations, setOrganisation, setOrganisations } =
    useOrganisationStore();

  const eventsPath = isSupportedLanguage(lang)
    ? getPagePath("events", lang)
    : PAGES.events;

  const applyOrganisation = (org: Organisation) => {
    setOrganisation(org);

    const currentUser = useAuthStore.getState().user;
    if (currentUser) {
      setUser({
        ...currentUser,
        organisation: {
          org_id: org.org_id,
          name: org.name,
          slug: org.slug,
          role: org.role,
        },
      });
    }
  };

  useEffect(() => {
    if (!user) {
      return;
    }

    const loadOrganisations = async () => {
      const response = (await appFetch(
        API_ROUTES.organisations.me,
      )) as ListUserOrganisationsResponse | null;

      if (!Array.isArray(response?.data)) {
        return;
      }

      setOrganisations(response.data);

      const active = useOrganisationStore.getState().organisation;
      const stillMember =
        active && response.data.some((org) => org.org_id === active.org_id);

      // The active organisation was revoked server-side: fall back to the
      // first organisation the user still belongs to.
      if (active && !stillMember && response.data.length > 0) {
        loggerService.warn(
          LogTag.STORE,
          "OrgSwitcher: active organisation no longer accessible, falling back",
          active.slug,
        );
        applyOrganisation(response.data[0]);
      }
    };

    void loadOrganisations();
  }, [user?.user_id]);

  if (!open || !user || organisations.length < 2) {
    return null;
  }

  const selected = organisations.find(
    (org) => org.org_id === organisation?.org_id,
  );

  const handleSelect = (next: Organisation | undefined) => {
    if (!next || next.org_id === organisation?.org_id) {
      return;
    }

    loggerService.info(LogTag.STORE, "OrgSwitcher: switch", next.slug);
    applyOrganisation(next);

    // Full reload: every store caches data scoped to the previous
    // organisation, so restart the app on the events page.
    window.location.assign(eventsPath);
  };

  const dropdown = (
    <StyledDropdown
      value={selected}
      setValue={handleSelect}
      itemList={organisations}
      label={t("nav.organisation")}
      itemLabelKey="name"
      itemIdKey="org_id"
      placeholder={t("nav.organisation")}
      width="100%"
    />
  );

  if (mobile) {
    return <div className="orgSwitcher mobile">{dropdown}</div>;
  }

  return <li className="orgSwitcher">{dropdown}</li>;
};

export default OrgSwitcher;
